import React, { useContext, useEffect, useRef, useState } from 'react';

import { app, media } from 'context';
import { Layout, Text } from 'ds';

/** Max width of the three-column KPI row; a bit wider so long numbers clear the dividers. */
const KPI_GRID_MAX_PX = 960;

interface KpiItem {
  target: number;
  format: 'comma' | 'prefixM';
  prefix?: string;
  startAt?: number;
  useCloserStart?: boolean;
  /** One-line caption on desktop and tablet. */
  label: React.ReactNode;
  /** Optional two-line caption when `isMobile` (narrow layout). */
  labelMobile?: React.ReactNode;
  delay: number;
  color: 'gray-900' | 'blue-500';
}

const KPI_ITEMS: ReadonlyArray<KpiItem> = [
  {
    target: 728000,
    format: 'comma',
    // Multiple digit columns differ from 728,000 so ones/tens/hundreds (and 1k) tick, not only 725→728.
    startAt: 722934,
    label: 'hours saved for teams',
    labelMobile: (
      <>
        hours saved
        <br />
        for teams
      </>
    ),
    delay: 0,
    color: 'gray-900'
  },
  {
    target: 1.3,
    format: 'prefixM',
    prefix: '$',
    useCloserStart: false,
    label: 'saved',
    delay: 500,
    color: 'blue-500'
  },
  {
    target: 15695,
    format: 'comma',
    startAt: 15645,
    label: 'office tasks resolved',
    labelMobile: (
      <>
        office tasks
        <br />
        resolved
      </>
    ),
    delay: 1000,
    color: 'gray-900'
  }
];

function formatDisplay(n: number, format: KpiItem['format'], prefix: string | undefined): string {
  if (format === 'prefixM') {
    return `${prefix ?? ''}${n.toFixed(1)}M`;
  }
  return Math.round(n).toLocaleString();
}

const TICKER_DURATION_MS = 1000;
const TICKER_STAGGER_MS = 52;

function alignTickerStrings(startFormatted: string, endFormatted: string): [string, string] {
  if (startFormatted.length === endFormatted.length) {
    return [startFormatted, endFormatted];
  }
  const m = Math.max(startFormatted.length, endFormatted.length);
  return [startFormatted.padStart(m, '\u2007'), endFormatted.padStart(m, '\u2007')];
}

function isDigitChar(c: string): boolean {
  return c >= '0' && c <= '9';
}

const TickerDigitCell: React.FC<{
  from: number;
  to: number;
  run: boolean;
  staggerMs: number;
  instant: boolean;
}> = ({ from, to, run, staggerMs, instant }) => {
  if (from === to) {
    return (
      <span
        style={{
          display: 'inline-block',
          minWidth: '1ch',
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {to}
      </span>
    );
  }

  const duration = instant ? 0 : TICKER_DURATION_MS;

  return (
    <span
      style={{
        display: 'inline-block',
        height: '1em',
        lineHeight: 1,
        overflow: 'hidden',
        verticalAlign: 'top',
        minWidth: '1ch',
        textAlign: 'center',
        fontVariantNumeric: 'tabular-nums'
      }}
    >
      <span
        style={{
          display: 'block',
          transform: run ? `translateY(-${to}em)` : `translateY(-${from}em)`,
          transition:
            run || instant
              ? `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${instant ? 0 : staggerMs}ms`
              : 'none',
          willChange: 'transform'
        }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
          <span key={d} style={{ display: 'block', height: '1em', lineHeight: 1 }}>
            {d}
          </span>
        ))}
      </span>
    </span>
  );
};

const KpiTickerNumber: React.FC<{ item: KpiItem; started: boolean }> = ({ item, started }) => {
  const baseStartValue = item.startAt ?? item.target * 0.8;
  const startValue =
    item.useCloserStart === false ? baseStartValue : baseStartValue + (item.target - baseStartValue) * 0.3;
  const startFormatted = formatDisplay(startValue, item.format, item.prefix);
  const endFormatted = formatDisplay(item.target, item.format, item.prefix);
  const [startAligned, endAligned] = alignTickerStrings(startFormatted, endFormatted);
  const [run, setRun] = useState(false);
  const [instant, setInstant] = useState(false);

  useEffect(() => {
    if (!started) return;

    const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const t = window.setTimeout(() => {
      if (reduced) {
        setInstant(true);
      }
      setRun(true);
    }, item.delay);

    return () => {
      window.clearTimeout(t);
      setRun(false);
      setInstant(false);
    };
  }, [item.delay, started]);

  const cols = startAligned.length;

  return (
    <Layout flex justify="center" width="100%" __style={{ marginBottom: 10 }}>
      <Text
        inline
        size="display-md"
        color={item.color}
        __style={{
          display: 'inline-flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'flex-start',
          fontSize: 'clamp(40px, 5vw, 64px)',
          fontWeight: 800,
          letterSpacing: '-0.04em',
          lineHeight: 1,
          marginTop: 0,
          minWidth: `${endFormatted.length}ch`,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {Array.from({ length: cols }, (_, i) => {
          const cs = startAligned[i] ?? '';
          const ce = endAligned[i] ?? '';
          const staggerMs = (cols - 1 - i) * TICKER_STAGGER_MS;

          if (isDigitChar(cs) && isDigitChar(ce)) {
            return (
              <TickerDigitCell
                key={i}
                from={Number(cs)}
                to={Number(ce)}
                run={run}
                staggerMs={staggerMs}
                instant={instant}
              />
            );
          }

          return (
            <span key={i} style={{ display: 'inline-block', fontVariantNumeric: 'tabular-nums' }}>
              {run ? ce : cs}
            </span>
          );
        })}
      </Text>
    </Layout>
  );
};

const HomeKpiSection: React.FC = () => {
  const { isMobile } = useContext(media);
  const { contentPaddingX } = useContext(app);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [started, setStarted] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setRevealed(true);
            setStarted(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Layout color="white" paddingY={isMobile ? 72 : 100} paddingX={contentPaddingX} position="relative">
      <div
        ref={sectionRef}
        id="kpis"
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Layout
          minWidth={0}
          __style={{
            display: 'grid',
            width: `min(100%, ${KPI_GRID_MAX_PX}px)`,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            gap: 0
          }}
        >
          {KPI_ITEMS.map((item, idx) => (
            <Layout
              key={idx}
              paddingX={isMobile ? 24 : 40}
              paddingY={isMobile ? 24 : 0}
              __style={{
                textAlign: 'center',
                borderRight: isMobile || idx === KPI_ITEMS.length - 1 ? undefined : '1px solid #e4e9ee',
                borderBottom: isMobile && idx < KPI_ITEMS.length - 1 ? '1px solid #e4e9ee' : undefined
              }}
            >
              <div
                className={`HomeV10-kpiReveal${revealed ? ' is-visible' : ''}`}
                style={{ transitionDelay: `${item.delay}ms` }}
              >
                <KpiTickerNumber item={item} started={started} />
                <Text size="body-md" color="gray-500" __style={{ lineHeight: 1.5, fontWeight: 400 }}>
                  {isMobile ? (item.labelMobile ?? item.label) : item.label}
                </Text>
              </div>
            </Layout>
          ))}
        </Layout>
      </div>
      <Layout position="absolute" __style={{ bottom: 0, left: '50%', transform: 'translateX(-50%)' }} aria-hidden>
        <svg width={2} height={64} viewBox="0 0 2 64">
          <line x1={1} y1={0} x2={1} y2={64} stroke="#e4e9ee" strokeWidth={1} strokeDasharray="3 4" />
        </svg>
      </Layout>
    </Layout>
  );
};

export default HomeKpiSection;
